import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ToothFullHeadMPRSliceSelection } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { toastErrors } from '@/shared/config';
import {
  MedicalImageRender,
  type FullHeadMPRProjection,
  type FullHeadMPRViewSnapshot,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { toastCaller } from '@/shared/ui';

import { reportsModel, useCheckReportSignature } from '@/entities/reports';

import type {
  FullHeadMprOriginalSlice,
  FullHeadMprReportSelectionResolver,
} from '../config';
import { makeFullHeadMprEditorStateRequest } from '../lib';

export const useAddFullHeadMprViewToReport = ({
  toothID,
  volumeFileID,
  getSnapshot = (projection) =>
    MedicalImageRender.isFullHeadMPRViewReady(projection)
      ? MedicalImageRender.getFullHeadMPRViewSnapshot(projection) ?? null
      : null,
  original,
  resolveSelection,
  snapshot,
}: {
  toothID: string;
  volumeFileID: string | null;
  original?: FullHeadMprOriginalSlice | null;
  snapshot?: FullHeadMPRViewSnapshot | null;
  getSnapshot?: (
    projection?: FullHeadMPRProjection,
  ) => FullHeadMPRViewSnapshot | null;
  resolveSelection?: FullHeadMprReportSelectionResolver;
}) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { checkReportSignature } = useCheckReportSignature();
  const isSaving = useAppSelector(
    reportsModel.selectors.selectIsFullHeadMprWritePending(toothID),
  );
  const slices = useAppSelector(
    reportsModel.selectors.selectFullHeadMprSlicesByToothID(toothID),
  );
  const report = useAppSelector(reportsModel.selectors.selectCurrentReport);
  const loading = useAppSelector(
    reportsModel.selectors.selectFullHeadMprLoading(report?.ID ?? ''),
  );
  const [isPending, setIsPending] = useState(false);
  const pendingRef = useRef(false);
  const latestRef = useRef({ slices, toothID, volumeFileID });
  latestRef.current = { slices, toothID, volumeFileID };
  const isDisabled =
    !toothID ||
    loading !== 'succeeded' ||
    !report?.YourPermissions?.CanChangeReportAssets ||
    !report.YourPermissions.CanChangeToothAssets;

  const getViewSelection = (currentSnapshot: FullHeadMPRViewSnapshot) => {
    const request = makeFullHeadMprEditorStateRequest({
      snapshot: currentSnapshot,
      toothID,
      slices,
      original,
    });
    const target = request.Target;
    const record =
      target.case === 'SliceID'
        ? slices.find((slice) => slice.ID === target.value)
        : undefined;
    const seriesSelection =
      record?.Source.case === 'View'
        ? null
        : resolveSelection?.(currentSnapshot) ?? null;
    return {
      record,
      seriesSelection,
      isSelected:
        seriesSelection?.isSelected ??
        record?.Selection ===
          ToothFullHeadMPRSliceSelection.ToothFullHeadMPRSliceSelectionAdded,
    };
  };
  const selection = snapshot ? getViewSelection(snapshot) : null;

  const changeSelection = ({
    projection,
    action,
  }: {
    action: 'add' | 'toggle';
    projection?: FullHeadMPRProjection;
  }) => {
    if (pendingRef.current || isSaving || isDisabled) {
      return;
    }
    const snapshot = getSnapshot(projection);
    if (!snapshot || snapshot.view.volumeFileID !== volumeFileID) {
      return;
    }
    const { record, seriesSelection, isSelected } = getViewSelection(snapshot);
    if (seriesSelection?.isDisabled || seriesSelection?.isLoading) {
      return;
    }
    if (action === 'toggle' && seriesSelection) {
      seriesSelection.onToggle();
      return;
    }
    pendingRef.current = true;
    setIsPending(true);
    const finish = () => {
      pendingRef.current = false;
      setIsPending(false);
    };
    checkReportSignature({
      toothID,
      onCancel: finish,
      onSignatureChecked: async () => {
        try {
          if (action === 'toggle' && record && isSelected) {
            await dispatch(
              reportsModel.thunks.setToothFullHeadMPRSliceSelection({
                ToothID: toothID,
                IsAdded: false,
                Target: { case: 'SliceID', value: record.ID },
              }),
            );
            return;
          }
          const seriesOriginal = await seriesSelection?.getOriginalSlice();
          if (
            latestRef.current.toothID !== toothID ||
            latestRef.current.volumeFileID !== volumeFileID
          ) {
            return;
          }
          const editorRequest = makeFullHeadMprEditorStateRequest({
            snapshot,
            toothID,
            slices: latestRef.current.slices,
            original: seriesOriginal ?? original,
          });
          const saved = await dispatch(
            reportsModel.thunks.setToothFullHeadMPRSliceEditorState(
              editorRequest,
            ),
          );
          if (
            reportsModel.thunks.setToothFullHeadMPRSliceEditorState.fulfilled.match(
              saved,
            ) &&
            saved.payload.SliceID
          ) {
            await dispatch(
              reportsModel.thunks.setToothFullHeadMPRSliceSelection({
                ToothID: toothID,
                IsAdded: true,
                Target: { case: 'SliceID', value: saved.payload.SliceID },
              }),
            );
          }
        } catch {
          toastCaller({
            type: 'error',
            heading: formatMessage(toastErrors.errorHeading),
            message: formatMessage(toastErrors.defaultError),
            autoClose: 10000,
          });
        } finally {
          finish();
        }
      },
    });
  };

  const getAddToReportProps = ({
    isViewReady,
    snapshot,
  }: {
    isViewReady: boolean;
    snapshot: FullHeadMPRViewSnapshot;
  }) => {
    const { seriesSelection } = getViewSelection(snapshot);
    return {
      isLoading: isPending || isSaving || !!seriesSelection?.isLoading,
      isDisabled:
        isDisabled ||
        !isViewReady ||
        snapshot.view.volumeFileID !== volumeFileID ||
        !!seriesSelection?.isDisabled,
      onAddToReport: () =>
        changeSelection({
          action: 'add',
          projection: snapshot.view.projection,
        }),
    };
  };

  return {
    isSelected: selection?.isSelected ?? false,
    isLoading: isPending || isSaving || !!selection?.seriesSelection?.isLoading,
    isDisabled:
      isDisabled ||
      !snapshot ||
      snapshot.view.volumeFileID !== volumeFileID ||
      !!selection?.seriesSelection?.isDisabled,
    getAddToReportProps,
    onToggleSelection: () => changeSelection({ action: 'toggle' }),
  };
};
